// src/pages/CekStatus.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CekStatus.css";

const STATUS_CONFIG = {
  baru:     { label: "Menunggu",  className: "badge-menunggu" },
  masuk:    { label: "Menunggu",  className: "badge-menunggu" },
  diproses: { label: "Diproses",  className: "badge-diproses" },
  selesai:  { label: "Selesai",   className: "badge-selesai"  },
  ditolak:  { label: "Ditolak",   className: "badge-ditolak"  },
};

function buildTimeline(status, createdAt, updatedAt) {
  const fmt = (iso) =>
    new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).replace(".", ":");

  const steps = [
    { label: "Laporan diterima",         doneOn: ["baru","masuk","diproses","selesai","ditolak"] },
    { label: "Sedang diproses oleh tim", doneOn: ["diproses","selesai","ditolak"] },
    { label: "Selesai ditangani",        doneOn: ["selesai"] },
  ];

  return steps.map((s, i) => {
    const done = s.doneOn.includes(status);
    let waktu = null;
    if (done) waktu = i === 0 ? fmt(createdAt) : fmt(updatedAt);
    return { label: s.label, waktu, done };
  });
}

function getFileType(nama) {
  const ext = nama.split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['zip','rar'].includes(ext)) return 'zip';
  if (['doc','docx'].includes(ext)) return 'word';
  if (['xls','xlsx'].includes(ext)) return 'excel';
  return 'file';
}
const FILE_EMOJI = { image:'🖼️', pdf:'📄', zip:'🗜️', word:'📝', excel:'📊', file:'📎' };

/* ─── Komponen lampiran item (fix: tidak pakai onError toggle, pakai state) ── */
function LampiranItem({ f }) {
  const tipe = getFileType(f.nama);
  const isImg = tipe === 'image';
  const [imgError, setImgError] = useState(false);

  return (
    <li className="cs-lampiran-item">
      {isImg && !imgError ? (
        <img
          src={f.url}
          alt={f.nama}
          className="cs-lampiran-thumb"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="cs-lampiran-emoji">
          {FILE_EMOJI[tipe]}
        </span>
      )}
      <span className="cs-lampiran-nama">{f.nama}</span>
      <div className="cs-lampiran-actions">
        {isImg && !imgError && (
          <a href={f.url} target="_blank" rel="noreferrer" className="cs-lampiran-btn" title="Lihat">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
          </a>
        )}
        <a href={f.url} download={f.nama} className="cs-lampiran-btn" title="Unduh">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        </a>
      </div>
    </li>
  );
}

/* ─── Komponen LampiranSection ── */
function LampiranSection({ files, title, accent }) {
  if (!files || files.length === 0) return null;
  return (
    <div className={`cs-lampiran-box ${accent}`}>
      <div className="cs-lampiran-header">
        <span className="cs-lampiran-title">{title}</span>
        <span className="cs-lampiran-count">{files.length} file</span>
      </div>
      <ul className="cs-lampiran-list">
        {files.map((f, i) => (
          <LampiranItem key={i} f={f} />
        ))}
      </ul>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════ */
const CekStatus = () => {
  const navigate = useNavigate();
  const [inputKode, setInputKode] = useState("");
  const [result,    setResult]    = useState(null);
  const [notFound,  setNotFound]  = useState(false);
  const [searched,  setSearched]  = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg,  setErrorMsg]  = useState("");

  const handleCek = async () => {
    const trimmed = inputKode.trim().toUpperCase();
    if (!trimmed) return;

    setIsLoading(true);
    setSearched(false);
    setResult(null);
    setNotFound(false);
    setErrorMsg("");

    try {
      const res  = await fetch(`/api/pengaduan/cek/?kode=${encodeURIComponent(trimmed)}`);
      const data = await res.json();
      setSearched(true);

      if (res.status === 404) { setNotFound(true); return; }
      if (!res.ok) { setErrorMsg(data.message || "Terjadi kesalahan."); return; }

      setResult(data.pengaduan);
    } catch {
      setSearched(true);
      setErrorMsg("Tidak dapat terhubung ke server.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleCek(); };

  const statusCfg = result ? STATUS_CONFIG[result.status] || STATUS_CONFIG["baru"] : null;
  const timeline  = result ? buildTimeline(result.status, result.created_at, result.updated_at) : [];

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("id-ID", { day:"2-digit", month:"short", year:"numeric" });

  const fmtDateTime = (iso) =>
    new Date(iso).toLocaleString("id-ID", {
      day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit",
    }).replace(".", ":");

  return (
    <div className="cs-wrapper">
      {/* NAVBAR */}
      <nav className="cs-navbar">
        <button className="cs-btn-back" onClick={() => navigate("/")}>← Kembali</button>
        <img src="/logolemhannas.png" alt="Logo" className="cs-logo" />
        <button className="cs-btn-login" onClick={() => navigate("/login")}>Login</button>
      </nav>

      <main className="cs-main">
        <div className="cs-badge-wrap">
          <span className="cs-top-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            CEK STATUS LAPORAN
          </span>
        </div>

        <h1 className="cs-title">Pantau status laporan</h1>
        <p className="cs-subtitle">
          Masukkan kode laporan yang kamu terima<br />saat mengirim pengaduan.
        </p>

        {/* Search */}
        <div className="cs-search-row">
          <input type="text" className="cs-input"
            placeholder="Contoh: PGD-2026-12345"
            value={inputKode}
            onChange={e => setInputKode(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading} />
          <button className="cs-btn-cek" onClick={handleCek}
            disabled={isLoading || !inputKode.trim()}>
            {isLoading ? <span className="cs-spinner" /> : "Cek Status"}
          </button>
        </div>

        {errorMsg && <div className="cs-error-banner">⚠️ {errorMsg}</div>}

        {/* Empty state */}
        {!searched && !isLoading && (
          <div className="cs-empty-state">
            <div className="cs-empty-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <rect x="10" y="6" width="36" height="44" rx="4" fill="#e8eaf0"/>
                <rect x="10" y="6" width="36" height="10" rx="4" fill="#c5c9d8"/>
                <rect x="18" y="22" width="20" height="2.5" rx="1.25" fill="#c5c9d8"/>
                <rect x="18" y="28" width="14" height="2.5" rx="1.25" fill="#c5c9d8"/>
                <rect x="18" y="34" width="17" height="2.5" rx="1.25" fill="#c5c9d8"/>
                <circle cx="28" cy="11" r="3" fill="#a0a5b8"/>
              </svg>
            </div>
            <p className="cs-empty-text">Masukkan kode laporan di atas<br/>untuk melihat status terkini.</p>
            <p className="cs-empty-hint">Format kode: PGD-2026-XXXXX</p>
          </div>
        )}

        {/* Not found */}
        {searched && notFound && (
          <div className="cs-empty-state">
            <div className="cs-empty-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="22" fill="#fee2e2"/>
                <path d="M16 16l16 16M32 16L16 32" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="cs-empty-text">Kode laporan tidak ditemukan.</p>
            <p className="cs-empty-hint">Pastikan kode yang Anda masukkan benar.</p>
          </div>
        )}

        {/* RESULT */}
        {searched && result && (
          <div className="cs-result-card">
            <div className="cs-card-header">
              <span className="cs-kode-badge">{result.kode_unik}</span>
              <span className={`cs-status-badge ${statusCfg.className}`}>● {statusCfg.label}</span>
            </div>

            <div className="cs-card-body">
              <h2 className="cs-laporan-judul">{result.judul}</h2>
              <p className="cs-laporan-desc">{result.deskripsi}</p>

              <div className="cs-meta-row">
                <div className="cs-meta-item">
                  <span className="cs-meta-label">PELAPOR</span>
                  <span className="cs-meta-value">{result.nama_pelapor || "Anonim"}</span>
                </div>
                <div className="cs-meta-item">
                  <span className="cs-meta-label">TANGGAL MASUK</span>
                  <span className="cs-meta-value">{fmtDate(result.created_at)}</span>
                </div>
                <div className="cs-meta-item">
                  <span className="cs-meta-label">TERAKHIR DIPERBARUI</span>
                  <span className="cs-meta-value">{fmtDateTime(result.updated_at)}</span>
                </div>
                {result.ditangani_oleh && (
                  <div className="cs-meta-item">
                    <span className="cs-meta-label">DITANGANI OLEH</span>
                    <span className="cs-meta-value">{result.ditangani_oleh.nama_lengkap}</span>
                  </div>
                )}
              </div>

              <hr className="cs-divider" />

              {/* ── Lampiran dari pelapor ── */}
              <LampiranSection
                files={result.lampiran}
                title="📎 Lampiran dari Pelapor"
                accent="cs-lampiran-pelapor"
              />

              {/* Timeline */}
              <h3 className="cs-timeline-title">Riwayat penanganan</h3>
              <div className="cs-timeline">
                {timeline.map((item, i) => (
                  <div key={i} className={`cs-timeline-item ${item.done ? "done" : "pending"}`}>
                    <div className="cs-timeline-left">
                      <div className={`cs-timeline-circle ${item.done ? "done" : "pending"}`}>
                        {item.done ? (
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M3 8l3.5 3.5L13 4.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : <div className="cs-timeline-dot-inner" />}
                      </div>
                      {i < timeline.length - 1 && (
                        <div className={`cs-timeline-line ${timeline[i+1].done ? "done" : "pending"}`} />
                      )}
                    </div>
                    <div className="cs-timeline-content">
                      <p className={`cs-timeline-label ${item.done ? "done" : "pending"}`}>{item.label}</p>
                      {item.waktu && <p className="cs-timeline-time">{item.waktu}</p>}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Lampiran balasan admin ── */}
              <LampiranSection
                files={result.lampiran_admin}
                title="📤 Lampiran Balasan Admin"
                accent="cs-lampiran-admin"
              />

              {/* Catatan admin */}
              {result.catatan_admin && (
                <div className="cs-balasan-box">
                  <div className="cs-balasan-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    <p className="cs-balasan-label">CATATAN DARI ADMIN</p>
                  </div>
                  <p className="cs-balasan-text">{result.catatan_admin}</p>
                  {result.ditangani_oleh && (
                    <p className="cs-balasan-by">— {result.ditangani_oleh.nama_lengkap}</p>
                  )}
                </div>
              )}

              {result.status === "ditolak" && !result.catatan_admin && (
                <div className="cs-balasan-box cs-balasan-ditolak">
                  <div className="cs-balasan-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <p className="cs-balasan-label">LAPORAN DITOLAK</p>
                  </div>
                  <p className="cs-balasan-text">Laporan Anda tidak dapat diproses. Hubungi admin untuk informasi lebih lanjut.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CekStatus;